import TipBox from "../../Common/TipBox";
import Panel from "../../Common/Panel";
import store from "../../Common/Store";
import RadionBtnBlock from "./RadionBtnBlock";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

function FloorType(props) {
  // 1 = Tile/Stone
  // 2 = Laminate / engineered board
  // 3 = (unused – previously laminate non-floating)
  // 4 = Vinyl / LVT
  // 5 = Carpet
  const [floorCovering, setFloorCovering] = useState(1);

  // 6 = 160W mats, 7 = 200W mats, 9 = 240W mats
  const [heatOutput, setHeatOutput] = useState(6);

  // Mat vs cable (kept for compatibility)
  const [floorType, setFloorType] = useState(1);
  const [page, setPageState] = useState("/");
  const { t, i18n } = useTranslation();
  const [hoveredId, setHoveredId] = useState(null);
  const [infoExpanded, setInfoExpanded] = useState(false);

  const isTileStone = floorCovering === 1;

  // Map covering + heat to the value used by the calculator
  const getStoreFloorValue = (covering, heat) => {
    if (covering === 1) {
      return heat;
    }
    // Other finishes always use 160W mats
    return 6;
  };

  // Apply selection into shared store
  useEffect(() => {
    store.thermo = [];
    store.floor1 = getStoreFloorValue(floorCovering, heatOutput);
    store.floorType = floorType;
  }, [floorCovering, heatOutput, floorType]);

  // Work out base URL for prev/next and sync language
  useEffect(() => {
    const url = new URL(window.location.href);
    const c = url.searchParams.get("fig_type");
    const img = url.searchParams.get("img");

    if (c) {
      setPageState("/standartmode?fig_type=" + parseInt(c, 10));
    } else if (img) {
      setPageState("/imgmode?");
    } else {
      setPageState("/standartmode");
    }

    i18n.changeLanguage(localStorage.getItem("lang"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Clear thermostats when entering this step
  useEffect(() => {
    store.thermo = [];
  }, []);

  // Styles
  const getFloorCardStyle = ({ id, selected }) => {
    const isHovered = hoveredId === id;

    return {
      flexDirection: "row-reverse",
      position: "relative",
      width: "200px",
      height: "95px",
      padding: "10px 12px",
      marginBottom: "5px",
      borderRadius: "22px",
      cursor: "pointer",
      userSelect: "none",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      background: selected
        ? "rgba(255,184,0,0.12)"
        : isHovered
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.1)",
      border: selected
        ? "2px solid #FFB800"
        : "1px solid rgba(255,255,255,0.15)",
      transition:
        "border 0.15s ease, background 0.15s ease, transform 0.1s ease",
      transform: selected ? "translateY(-1px)" : "none",
    };
  };

  const getHeatCardStyle = ({ id, selected }) => {
    const isHovered = hoveredId === id;

    return {
      position: "relative",
      width: "150px",
      height: "180px",
      padding: "14px 10px 18px",
      borderRadius: "24px",
      cursor: "pointer",
      userSelect: "none",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      background: selected
        ? "rgba(255,184,0,0.12)"
        : isHovered
        ? "rgba(255,255,255,0.03)"
        : "transparent",
      border: selected
        ? "2px solid #FFB800"
        : "1px solid rgba(255,255,255,0.2)",
      transition:
        "border 0.15s ease, background 0.15s ease, transform 0.1s ease",
      top: "53px",
    };
  };

  // Round inputs for the left-hand floor cards
  const floorRadioStyle = {
    position: "absolute",
    left: "-15px",
    top: "33px",
  };

  const heatRadioStyle = {
    position: "absolute",
    left: "50%",
    transform: "translateX(-20%)",
    bottom: "6px",
  };

  // Connector line styles for tile/stone heat output cards
  const getConnectorStyles = (heat) => {
    const colour = "rgb(255, 184, 0)";

    const baseWrapper = {
      position: "absolute",
      pointerEvents: "none",
      transition: "all 0.3s ease",
    };

    const baseHorizontal = {
      position: "absolute",
      borderTop: `2px solid ${colour}`,
      transition: "all 0.3s ease",
    };

    const baseVertical = {
      position: "absolute",
      borderLeft: `2px solid ${colour}`,
      transition: "all 0.3s ease",
    };

    if (heat === 7) {
      // 200W state
      return {
        wrapper: {
          ...baseWrapper,
          top: 22,
          left: -41,
          width: 284,
          height: 40,
        },
        horizontal: {
          ...baseHorizontal,
          left: 0,
          top: 18,
          width: "100%",
        },
        vertical: {
          ...baseVertical,
          left: 283,
          top: 18,
          height: 12,
        },
      };
    }

    if (heat === 9) {
      // 240W state
      return {
        wrapper: {
          ...baseWrapper,
          top: 22,
          left: -41,
          width: 454,
          height: 40,
        },
        horizontal: {
          ...baseHorizontal,
          left: 0,
          top: 18,
          width: "100%",
        },
        vertical: {
          ...baseVertical,
          left: 454,
          top: 18,
          height: 12,
        },
      };
    }

    // Default / 160W state
    return {
      wrapper: {
        ...baseWrapper,
        top: 22,
        left: -40,
        width: 114,
        height: 80,
      },
      horizontal: {
        ...baseHorizontal,
        left: 0,
        top: 18,
        width: "100%",
      },
      vertical: {
        ...baseVertical,
        right: 0,
        top: 18,
        height: 12,
      },
    };
  };

  // Connector line styles for info boxes (non-tile floors)
  const getInfoConnectorStyles = (covering) => {
    const colour = "rgb(255, 184, 0)";

    const baseWrapper = {
      position: "absolute",
      pointerEvents: "none",
      transition: "all 0.3s ease",
    };

    const baseHorizontal = {
      position: "absolute",
      borderTop: `2px solid ${colour}`,
      transition: "all 0.3s ease",
    };

    const baseVertical = {
      position: "absolute",
      borderLeft: `2px solid ${colour}`,
      transition: "all 0.3s ease",
    };

    // Order of cards beneath Tile/Stone
    const orderMap = {
      2: 1, // Laminate / engineered
      4: 2, // Vinyl / LVT
      5: 3, // Carpet
    };

    const rowIndex = orderMap[covering] || 1;
    const rowHeight = 100; // 95px height + 5px margin
    const topOffset = 22 + rowHeight * rowIndex;

    // Default vertical for laminate / engineered (covering 2)
    let verticalTop = 18;
    let verticalHeight = 12;

    // Custom verticals for Vinyl/LVT and Carpet
    if (covering === 4) {
      // Vinyl / LVT
      verticalTop = -31;
      verticalHeight = 51;
    } else if (covering === 5) {
      // Carpet
      verticalTop = -100;
      verticalHeight = 120;
    }

    return {
      wrapper: {
        ...baseWrapper,
        top: topOffset,
        left: -41,
        width: 323,
        height: 40,
        zIndex: 0,
      },
      horizontal: {
        ...baseHorizontal,
        left: 0,
        top: 18,
        width: "100%",
      },
      vertical: {
        ...baseVertical,
        left: 322,
        top: verticalTop,
        height: verticalHeight,
      },
    };
  };

  const infoBoxStyle = {
    position: "relative",
    marginTop: "40px",
    marginLeft: "80px",
    width: "410px",
    minHeight: "170px",
    padding: "18px 22px",
    borderRadius: "24px",
    border: "2px solid #FFB800",
    background: "#7d7d7d",
    boxSizing: "border-box",
    color: "#FFFFFF",
    fontSize: "13px",
    lineHeight: 1.5,
    zIndex: 1,
  };

  const handleFloorSelect = (value) => {
    setFloorCovering(value);
    setInfoExpanded(false);
    if (value !== 1) {
      setHeatOutput(6); // ensure underlying 160W for non-tile floors
    }
  };

  const handleFloorKeyDown = (e, value) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleFloorSelect(value);
    }
  };

  const handleHeatSelect = (value) => {
    setHeatOutput(value);
  };

  const handleHeatKeyDown = (e, value) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleHeatSelect(value);
    }
  };

  // Info text for non-tile floors
  const getInfoContent = (covering) => {
    if (covering === 2) {
      return {
        title: t("Laminate / engineered board"),
        body: t("Laminate floor information"),
      };
    }

    if (covering === 4) {
      return {
        title: t("Vinyl / LVT"),
        body: t("Vinyl floor information"),
      };
    }

    if (covering === 5) {
      return {
        title: t("Carpet"),
        body: t("Carpet floor information"),
      };
    }

    // Fallback (should not normally be seen)
    return {
      title: "",
      body: "",
    };
  };

  return (
    <>
      <div>
        {/* Hidden heading kept for accessibility */}
        <h1
          style={{
            color: "#5F5F5F",
            fontSize: "24px",
            fontWeight: 400,
            textAlign: "center",
            top: "-700px",
            bottom: 0,
            left: "160px",
            right: 0,
            position: "absolute",
            margin: "auto",
            height: "40px",
            display: "none",
          }}
        >
          {t("Choose Floor")}
        </h1>

        <div
          className="floor-type-stage"
          style={{
            height: "500px",
            position: "absolute",
            zIndex: 10,
            bottom: 0,
            left: "160px",
            right: 0,
            top: "-175px",
            margin: "auto",
          }}
        >
          <div
            className="floor-type-content"
            style={{
              width: "840px",
              background: "transparent",
              margin: "0 auto",
            }}
          >
            {/* Header */}
            <RadionBtnBlock
              className="floor-type-panel"
              style={{ background: "transparent", width: "840px" }}
              header={
                <div
                  className="floor-type-header"
                  style={{
                    background: "transparent",
                    textAlign: "center",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      color: "#FFB800",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    {t("Floor finish")}
                  </div>
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: 600,
                      color: "#FFFFFF",
                    }}
                  >
                    {t("Select the floor type for this room")}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255,255,255,0.8)",
                      marginTop: "4px",
                      marginBottom: "10px",
                    }}
                  >
                    {t(
                      "For tile and stone you can also choose the heat output. Other finishes use 160W heating mats."
                    )}
                  </div>
                </div>
              }
            >
              {/* Main layout: left = floor types, right = heat output or info box */}
              <div
                className="floor-type-layout"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: "40px",
                  marginTop: "10px",
                }}
              >
                {/* LEFT – floor type list */}
                <div
                  className="floor-type-options"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Tile / Stone */}
                  <div
                    className="floor-type-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFloorSelect(1)}
                    onKeyDown={(e) => handleFloorKeyDown(e, 1)}
                    onMouseEnter={() => setHoveredId("floor1")}
                    onMouseLeave={() => setHoveredId(null)}
                    style={getFloorCardStyle({
                      id: "floor1",
                      selected: floorCovering === 1,
                    })}
                    aria-pressed={floorCovering === 1}
                  >
                    <div
                      className="floor-type-card__image"
                      style={{
                        width: "70px",
                        height: "70px",
                        borderRadius: "18px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        alt={t("Tile or Stone")}
                        src="img/floor6.png"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        color: "white",
                        fontSize: "13px",
                        margin: "0 4px",
                        textAlign: "right",
                        fontWeight: 400,
                      }}
                    >
                      {t("Tile / Stone")}
                    </p>
                    <div className="round" style={floorRadioStyle}>
                      <input
                        type="radio"
                        id="floor_radio1"
                        checked={floorCovering === 1}
                        name="floor_type"
                        onChange={(e) =>
                          e.target.checked && handleFloorSelect(1)
                        }
                      />
                      <label htmlFor="floor_radio1"></label>
                    </div>
                  </div>

                  {/* Laminate / engineered board (single option now) */}
                  <div
                    className="floor-type-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFloorSelect(2)}
                    onKeyDown={(e) => handleFloorKeyDown(e, 2)}
                    onMouseEnter={() => setHoveredId("floor2")}
                    onMouseLeave={() => setHoveredId(null)}
                    style={getFloorCardStyle({
                      id: "floor2",
                      selected: floorCovering === 2,
                    })}
                    aria-pressed={floorCovering === 2}
                  >
                    <div
                      className="floor-type-card__image"
                      style={{
                        width: "70px",
                        height: "70px",
                        borderRadius: "18px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        alt={t("Laminate / engineered board")}
                        src="img/floor1.png"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        color: "white",
                        fontSize: "12px",
                        margin: "0 4px",
                        textAlign: "right",
                      }}
                    >
                      {t("Laminate / engineered board")}
                    </p>
                    <div className="round" style={floorRadioStyle}>
                      <input
                        type="radio"
                        id="floor_radio2"
                        checked={floorCovering === 2}
                        name="floor_type"
                        onChange={(e) =>
                          e.target.checked && handleFloorSelect(2)
                        }
                      />
                      <label htmlFor="floor_radio2"></label>
                    </div>
                  </div>

                  {/* Vinyl / LVT */}
                  <div
                    className="floor-type-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFloorSelect(4)}
                    onKeyDown={(e) => handleFloorKeyDown(e, 4)}
                    onMouseEnter={() => setHoveredId("floor4")}
                    onMouseLeave={() => setHoveredId(null)}
                    style={getFloorCardStyle({
                      id: "floor4",
                      selected: floorCovering === 4,
                    })}
                    aria-pressed={floorCovering === 4}
                  >
                    <div
                      className="floor-type-card__image"
                      style={{
                        width: "70px",
                        height: "70px",
                        borderRadius: "18px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        alt={t("Vinyl / LVT")}
                        src="img/floor3.png"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        color: "white",
                        fontSize: "12px",
                        margin: "0 4px",
                        textAlign: "right",
                      }}
                    >
                      {t("Vinyl / LVT")}
                    </p>
                    <div className="round" style={floorRadioStyle}>
                      <input
                        type="radio"
                        id="floor_radio4"
                        checked={floorCovering === 4}
                        name="floor_type"
                        onChange={(e) =>
                          e.target.checked && handleFloorSelect(4)
                        }
                      />
                      <label htmlFor="floor_radio4"></label>
                    </div>
                  </div>

                  {/* Carpet */}
                  <div
                    className="floor-type-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFloorSelect(5)}
                    onKeyDown={(e) => handleFloorKeyDown(e, 5)}
                    onMouseEnter={() => setHoveredId("floor5")}
                    onMouseLeave={() => setHoveredId(null)}
                    style={getFloorCardStyle({
                      id: "floor5",
                      selected: floorCovering === 5,
                    })}
                    aria-pressed={floorCovering === 5}
                  >
                    <div
                      className="floor-type-card__image"
                      style={{
                        width: "70px",
                        height: "70px",
                        borderRadius: "18px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        alt={t("Carpet")}
                        src="img/floor4.png"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        color: "white",
                        fontSize: "12px",
                        margin: "0 4px",
                        textAlign: "right",
                      }}
                    >
                      {t("Carpet")}
                    </p>
                    <div className="round" style={floorRadioStyle}>
                      <input
                        type="radio"
                        id="floor_radio5"
                        checked={floorCovering === 5}
                        name="floor_type"
                        onChange={(e) =>
                          e.target.checked && handleFloorSelect(5)
                        }
                      />
                      <label htmlFor="floor_radio5"></label>
                    </div>
                  </div>
                </div>

                {/* RIGHT – tile heat output cards OR info box for other floors */}
                <div
                  className="floor-type-detail"
                  style={{
                    flex: 1,
                    minHeight: "210px",
                    position: "relative",
                  }}
                >
                  {isTileStone ? (
                    <>
                      {/* Connector lines for tile/stone wattage cards */}
                      {(() => {
                        const connector = getConnectorStyles(heatOutput);
                        return (
                          <div className="floor-type-connector" style={connector.wrapper}>
                            <div style={connector.horizontal}></div>
                            <div style={connector.vertical}></div>
                          </div>
                        );
                      })()}

                      {/* Heat cards */}
                      <div
                        className="floor-heat-options"
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: "20px",
                          alignItems: "flex-start",
                        }}
                      >
                        {/* 160W mats – Normal Heat Loss (6) */}
                        <div
                          className="floor-heat-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleHeatSelect(6)}
                          onKeyDown={(e) => handleHeatKeyDown(e, 6)}
                          onMouseEnter={() => setHoveredId("heat6")}
                          onMouseLeave={() => setHoveredId(null)}
                          style={getHeatCardStyle({
                            id: "heat6",
                            selected: heatOutput === 6,
                          })}
                          aria-pressed={heatOutput === 6}
                        >
                          <div
                            className="floor-heat-card__image"
                            style={{
                              width: "100%",
                              height: "100px",
                              borderRadius: "22px",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              alt={t("160W mats")}
                              src="img/floor6.png"
                              style={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              color: "white",
                              fontSize: "15px",
                              margin: "10px 4px 0",
                              textAlign: "center",
                              fontWeight: 700,
                            }}
                          >
                            {t("160W mats")}
                            <br />
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "rgba(255,255,255,0.9)",
                              }}
                            >
                              {t("Normal Heat Loss")}
                            </span>
                          </p>
                          <div className="round" style={heatRadioStyle}>
                            <input
                              type="radio"
                              id="heat_radio6"
                              checked={heatOutput === 6}
                              name="heat_output"
                              onChange={(e) =>
                                e.target.checked && handleHeatSelect(6)
                              }
                            />
                            <label htmlFor="heat_radio6"></label>
                          </div>
                        </div>

                        {/* 200W mats – High Heat Loss (7) */}
                        <div
                          className="floor-heat-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleHeatSelect(7)}
                          onKeyDown={(e) => handleHeatKeyDown(e, 7)}
                          onMouseEnter={() => setHoveredId("heat7")}
                          onMouseLeave={() => setHoveredId(null)}
                          style={getHeatCardStyle({
                            id: "heat7",
                            selected: heatOutput === 7,
                          })}
                          aria-pressed={heatOutput === 7}
                        >
                          <div
                            className="floor-heat-card__image"
                            style={{
                              width: "100%",
                              height: "100px",
                              borderRadius: "22px",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              alt={t("200W mats")}
                              src="img/floor7.png"
                              style={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              color: "white",
                              fontSize: "15px",
                              margin: "10px 4px 0",
                              textAlign: "center",
                              fontWeight: 700,
                            }}
                          >
                            {t("200W mats")}
                            <br />
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "rgba(255,255,255,0.9)",
                              }}
                            >
                              {t("High Heat Loss")}
                            </span>
                          </p>
                          <div className="round" style={heatRadioStyle}>
                            <input
                              type="radio"
                              id="heat_radio7"
                              checked={heatOutput === 7}
                              name="heat_output"
                              onChange={(e) =>
                                e.target.checked && handleHeatSelect(7)
                              }
                            />
                            <label htmlFor="heat_radio7"></label>
                          </div>
                        </div>

                        {/* 240W mats – Very High Heat Loss (9) */}
                        <div
                          className="floor-heat-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleHeatSelect(9)}
                          onKeyDown={(e) => handleHeatKeyDown(e, 9)}
                          onMouseEnter={() => setHoveredId("heat9")}
                          onMouseLeave={() => setHoveredId(null)}
                          style={getHeatCardStyle({
                            id: "heat9",
                            selected: heatOutput === 9,
                          })}
                          aria-pressed={heatOutput === 9}
                        >
                          <div
                            className="floor-heat-card__image"
                            style={{
                              width: "100%",
                              height: "100px",
                              borderRadius: "22px",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              alt={t("240W mats")}
                              src="img/floor9.png"
                              style={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              color: "white",
                              fontSize: "15px",
                              margin: "10px 4px 0",
                              textAlign: "center",
                              fontWeight: 700,
                            }}
                          >
                            {t("240W mats")}
                            <br />
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "rgba(255,255,255,0.9)",
                              }}
                            >
                              {t("Very High Heat Loss")}
                            </span>
                          </p>
                          <div className="round" style={heatRadioStyle}>
                            <input
                              type="radio"
                              id="heat_radio9"
                              checked={heatOutput === 9}
                              name="heat_output"
                              onChange={(e) =>
                                e.target.checked && handleHeatSelect(9)
                              }
                            />
                            <label htmlFor="heat_radio9"></label>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Connector for info box */}
                      {(() => {
                        const connector = getInfoConnectorStyles(
                          floorCovering
                        );
                        return (
                          <div className="floor-type-connector" style={connector.wrapper}>
                            <div style={connector.horizontal}></div>
                            <div style={connector.vertical}></div>
                          </div>
                        );
                      })()}

                      {/* Info box content */}
                      {(() => {
                        const info = getInfoContent(floorCovering);
                        return (
                          <div className="floor-type-info" style={infoBoxStyle}>
                            {info.title && (
                              <div
                                style={{
                                  fontSize: 13,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.12em",
                                  color: "rgb(255, 184, 0)",
                                  fontWeight: 600,
                                  marginBottom: 4,
                                }}
                              >
                                {info.title}
                              </div>
                            )}
                            {info.body && (
                              <p
                                className={`floor-type-info__body${
                                  infoExpanded
                                    ? " floor-type-info__body--expanded"
                                    : ""
                                }`}
                                style={{
                                  margin: 0,
                                  fontSize: "13px",
                                  lineHeight: 1.5,
                                  color: "rgba(255,255,255,0.9)",
                                }}
                              >
                                {info.body}
                              </p>
                            )}
                            {info.body && (
                              <button
                                type="button"
                                className="floor-type-info__toggle"
                                onClick={() =>
                                  setInfoExpanded((expanded) => !expanded)
                                }
                                aria-expanded={infoExpanded}
                              >
                                {infoExpanded ? "Show less" : "Read more…"}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </RadionBtnBlock>

            {/* Hidden: Heating product (mat/cable) – unchanged */}
            <RadionBtnBlock
              header={
                <div
                  style={{
                    color: "#FFB800",
                    marginTop: "10px",
                    textAlign: "center",
                  }}
                >
                  {t("Heating product")}
                </div>
              }
              style={{ display: "none", width: "600px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "80px",
                    width: "242px",
                    marginRight: "20px",
                  }}
                >
                  <RadionBtnBlock
                    style={{ width: "100%", padding: "9px" }}
                    styleInner={{ height: "40px", alignItems: "center" }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{
                          color: "#FFB800",
                          marginRight: "26px",
                          fontSize: "18px",
                        }}
                      >
                        {t("Mat")}
                      </span>
                      <div
                        className="round"
                        style={{ top: 0, height: "28px", width: "30px" }}
                      >
                        <input
                          type="radio"
                          id="radio_btn1"
                          checked={floorType === 1}
                          onChange={(e) =>
                            e.target.checked && setFloorType(1)
                          }
                        />
                        <label htmlFor="radio_btn1"></label>
                      </div>
                    </div>
                  </RadionBtnBlock>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "80px",
                    width: "387px",
                  }}
                >
                  <RadionBtnBlock
                    style={{ width: "100%", padding: "9px" }}
                    styleInner={{
                      height: "40px",
                      alignItems: "center",
                      justifyContent: "none",
                      width: "auto",
                      marginLeft: "20px",
                    }}
                  >
                    <span
                      style={{
                        color: "#FFB800",
                        marginRight: "30px",
                        fontSize: "18px",
                      }}
                    >
                      {t("Cable")}
                    </span>
                    <span
                      style={{
                        color: "white",
                        fontSize: "18px",
                        marginRight: "10px",
                      }}
                    >
                      {t("5 mm")}
                    </span>
                    <div
                      className="round"
                      style={{
                        top: 0,
                        height: "28px",
                        width: "30px",
                        margin: 0,
                        inset: "0 0 0 0",
                      }}
                    >
                      <input
                        type="radio"
                        id="radio_btn2"
                        checked={floorType === 2}
                        onChange={(e) =>
                          e.target.checked && setFloorType(2)
                        }
                      />
                      <label htmlFor="radio_btn2"></label>
                    </div>
                    <span
                      style={{
                        color: "white",
                        fontSize: "18px",
                        marginRight: "10px",
                        marginLeft: "32px",
                      }}
                    >
                      {t("3 mm")}
                    </span>
                    <div
                      className="round"
                      style={{
                        top: 0,
                        height: "28px",
                        width: "30px",
                        margin: 0,
                        inset: "0 0 0 0",
                      }}
                    >
                      <input
                        type="radio"
                        id="radio_btn3"
                        checked={floorType === 3}
                        onChange={(e) =>
                          e.target.checked && setFloorType(3)
                        }
                      />
                      <label htmlFor="radio_btn3"></label>
                    </div>
                  </RadionBtnBlock>
                </div>
              </div>
            </RadionBtnBlock>
          </div>
        </div>
      </div>

      <Panel page="4" />
      {/* Navigation uses whatever is currently stored */}
      <TipBox tip="4" next={page + "&thermo=1"} prev={page + "&spot=1"} />
    </>
  );
}

export default FloorType;
